import React from "react"
import { navigate, type GatsbyBrowser } from "./src/compat/gatsby-public-api"
import Layout from "./src/layout"
import "./src/css/view-transitions.css"

declare global {
  interface Window {
    __pageTransitionNavigationInstalled?: boolean
  }
}

const FALLBACK_DURATION = 520
const MAX_NATIVE_TRANSITION_DURATION = FALLBACK_DURATION * 2

let activeTransition: ViewTransition | null = null
let nativeTransitionTimer: number | null = null
let fallbackTimer: number | null = null

interface MatchMediaProvider {
  readonly matchMedia: Window["matchMedia"]
}

const hasMatchMedia = (value: unknown): value is MatchMediaProvider =>
  typeof value === "object" &&
  value !== null &&
  "matchMedia" in value &&
  typeof value.matchMedia === "function"

const prefersReducedMotion = () => {
  const browserWindow: unknown = window

  return (
    hasMatchMedia(browserWindow) &&
    browserWindow.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

const clearNativeTransitionTimer = () => {
  if (nativeTransitionTimer === null) return

  window.clearTimeout(nativeTransitionTimer)
  nativeTransitionTimer = null
}

const clearTransitionState = (transition?: ViewTransition) => {
  if (transition && activeTransition !== transition) return

  clearNativeTransitionTimer()
  document.documentElement.removeAttribute("data-page-transition")
  activeTransition = null
}

const animateFallbackEntrance = () => {
  if (prefersReducedMotion()) return

  const page = document.querySelector<HTMLElement>(".layout-wrapper")
  if (!page) return

  if (fallbackTimer !== null) window.clearTimeout(fallbackTimer)
  page.classList.remove("page-transition-enter")
  void page.offsetWidth
  page.classList.add("page-transition-enter")

  fallbackTimer = window.setTimeout(() => {
    page.classList.remove("page-transition-enter")
  }, FALLBACK_DURATION)
}

const isModifiedClick = (event: MouseEvent) =>
  event.button !== 0 ||
  event.metaKey ||
  event.altKey ||
  event.ctrlKey ||
  event.shiftKey

const getInternalDestination = (event: MouseEvent) => {
  if (isModifiedClick(event) || event.defaultPrevented) return null
  if (!(event.target instanceof Element)) return null

  const anchor = event.target.closest<HTMLAnchorElement>("a[href]")
  if (!anchor || anchor.hasAttribute("download")) return null
  if (anchor.hasAttribute("data-no-view-transition")) return null
  if (anchor.target && anchor.target !== "_self") return null

  const destination = new URL(anchor.href, window.location.href)
  const current = new URL(window.location.href)

  if (destination.origin !== current.origin) return null
  if (
    destination.pathname === current.pathname &&
    destination.search === current.search
  ) {
    return null
  }

  return `${destination.pathname}${destination.search}${destination.hash}`
}

const navigateWithViewTransition = (destination: string) => {
  activeTransition?.skipTransition()
  clearNativeTransitionTimer()

  document.documentElement.setAttribute("data-page-transition", "running")

  try {
    const transition = document.startViewTransition(() => {
      navigate(destination)
    })
    activeTransition = transition

    // skipTransition() rejects `ready` when the transition has not started yet.
    // Observe that expected rejection so rapid navigations do not surface an
    // unhandled promise rejection while the destination still updates.
    void transition.ready.catch(() => undefined)

    nativeTransitionTimer = window.setTimeout(() => {
      if (activeTransition === transition) transition.skipTransition()
    }, MAX_NATIVE_TRANSITION_DURATION)

    void transition.finished.then(
      () => {
        clearTransitionState(transition)
      },
      () => {
        clearTransitionState(transition)
      }
    )
  } catch {
    clearTransitionState()
    navigate(destination)
  }
}

const handleNavigationClick = (event: MouseEvent) => {
  if (
    prefersReducedMotion() ||
    typeof document.startViewTransition !== "function"
  ) {
    return
  }

  const destination = getInternalDestination(event)
  if (!destination) return

  event.preventDefault()
  navigateWithViewTransition(destination)
}

export const onClientEntry: NonNullable<
  GatsbyBrowser["onClientEntry"]
> = () => {
  if (window.__pageTransitionNavigationInstalled) return

  document.addEventListener("click", handleNavigationClick, true)
  window.__pageTransitionNavigationInstalled = true
}

export const onRouteUpdate: NonNullable<GatsbyBrowser["onRouteUpdate"]> = ({
  prevLocation,
}) => {
  if (activeTransition) return
  if (prevLocation) animateFallbackEntrance()
}

export const shouldUpdateScroll: NonNullable<
  GatsbyBrowser["shouldUpdateScroll"]
> = ({ prevRouterProps, routerProps: { location } }) => {
  if (location.hash) return decodeURI(location.hash.slice(1))

  if (prevRouterProps?.location.pathname === location.pathname) {
    return [0, 0]
  }

  return true
}

export const wrapPageElement: NonNullable<GatsbyBrowser["wrapPageElement"]> = ({
  element,
  props,
}) => <Layout location={props.location}>{element}</Layout>
